import { useCreateShareLink } from "@/hooks/useShareLinks"
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useApp } from "../../AppProvider"
import Modal from "./Modal"

interface ShareLink {
  token: string
  resourceType: string
  resourceId: string
  organizationId: string
  createdBy: string
  createdAt: string
}

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  resourceType: string
  resourceId: string
  title?: string
}

export const ShareModal = ({ open, setOpen, resourceType, resourceId, title = "Share" }: Props) => {
  const { organizationId } = useParams()
  const { token } = useApp()
  const [copySuccess, setCopySuccess] = useState(false)
  const [shareUrl, setShareUrl] = useState<string>("")

  const createShareLinkMutation = useCreateShareLink(organizationId!, token)

  // Create share link when modal opens
  useEffect(() => {
    if (open && !shareUrl && !createShareLinkMutation.isPending) {
      createShareLinkMutation
        .mutateAsync({
          resourceType,
          resourceId,
        })
        .then((shareLink: ShareLink) => {
          const baseUrl = window.location.origin
          const url = `${baseUrl}/share/${shareLink.token}`
          setShareUrl(url)
        })
        .catch((error: Error) => {
          console.error("Failed to create share link:", error)
        })
    }
  }, [open, shareUrl, resourceType, resourceId, createShareLinkMutation])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setShareUrl("")
      setCopySuccess(false)
    }
  }, [open])

  const handleCopyLink = async () => {
    if (!shareUrl) return

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopySuccess(true)

      // Reset copy success message after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (error) {
      console.error("Failed to copy to clipboard:", error)
    }
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)}>
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Share this link to allow others to view this content without signing in.
          </p>

          {createShareLinkMutation.isPending && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="border-primary-600 h-4 w-4 animate-spin rounded-full border-b-2"></div>
              Creating share link...
            </div>
          )}

          {shareUrl && (
            <div className="space-y-3">
              <div className="rounded-md border bg-gray-50 p-3">
                <p className="font-mono text-sm break-all text-gray-900">{shareUrl}</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyLink}
                  className="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-none">
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {copySuccess ? "Copied!" : "Copy Link"}
                </button>

                {copySuccess && (
                  <span className="text-sm font-medium text-green-600">Link copied to clipboard!</span>
                )}
              </div>
            </div>
          )}

          {createShareLinkMutation.isError && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">Failed to create share link. Please try again.</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
